/**
 * Datenbasis der Seite „Liquiditätsplanung": USD-Raten offener Bestellungen, eingeordnet nach
 * Verlässlichkeit des Fälligkeitsdatums (fix / geplant / offen), und die Wochenreihe gegen
 * Kontostand und offene Kurssicherungen.
 *
 * NEUBAU ohne Bestandsvorbild in `index.html` (die Seite gibt es dort nicht); deshalb steht dieses
 * Modul nicht in den Herkunftslisten (`scripts/gen-herkunft.ts`).
 *
 * Reine Funktionen: die Uhr kommt als Argument `heute` (ISO-Datum), nie aus `todayIso()` - so sind
 * Wochenzuordnung und „überfällig" mit fester Uhr prüfbar.
 *
 * ================= Warum eigene Fälligkeitsermittlung =================
 *
 * Vom Server geladene Ratenzeilen tragen `dueDate` statt `due` (siehe AGENTS.md, „due/dueDate").
 * `faelligkeitVonRate` und `payAmount` lesen `due`. Deshalb wird je Rate eine KOPIE mit
 * `due = p.due || p.dueDate || ""` gerechnet; die Rate im Store bleibt unangetastet.
 *
 * ================= Fremde Felder =================
 *
 * `order.stockOrder` und `order.transportDatesFixed` führt ein paralleler Strang (Bestellfelder,
 * Drawer). Sie werden hier nur über die Hilfsfunktionen `istStockOrder`/`liefertermineFixiert`
 * gelesen (strikt `=== true`), damit dieses Modul vor dem Merge dieses Strangs schon läuft:
 * fehlende Felder zählen als `false`.
 */
import { isoWeekInfo, type IsoWeekInfo } from "./format.ts";
import { faelligkeitVonRate, fehlendesAnkerfeld, tageAddieren, wartetAufAbruf, type Anker, type Ankerdaten, type Basis } from "./anker.ts";
import { dealValid, type FxDeal } from "./fx.ts";
import { isPaid, orderTotal, payAmount, paymentStatusOf } from "./payments.ts";
import { activeReportingOrders } from "./status.ts";
import { orderCurrency } from "./suppliers.ts";
import type { Order, OrderPayment, Supplier } from "./types.ts";

export type LiquiditaetsKlasse = "fix" | "geplant" | "offen";
export type Transportart = "see" | "air" | "road";

export interface LiquiditaetsRate {
  /** Eindeutig je Zeile (Bestellung + Rate), stabil für React-Schlüssel. */
  schluessel: string;
  order: Order;
  orderId: string;
  /** `orderNo || name` - die lesbare Bestellkennung. */
  orderNo: string;
  lieferant: string;
  label: string;
  usd: number;
  /** ISO-Datum oder `null`, wenn keine Fälligkeit berechenbar ist. */
  datum: string | null;
  /** `null`, wenn `datum` fehlt. */
  kw: IsoWeekInfo | null;
  klasse: LiquiditaetsKlasse;
  /** Kurze Begründung der Einordnung für die Tabelle. */
  herkunft: string;
  anchor: Anker | null;
  stock: boolean;
  transportMode: Transportart;
  /** Datum liegt vor `heute`. */
  ueberfaellig: boolean;
}

/** Lesezugriff auf das Feld des parallelen Strangs; fehlt es, ist es `false`. */
export function istStockOrder(order: Order): boolean {
  return (order as Record<string, unknown>).stockOrder === true;
}

/** Lesezugriff auf das Feld des parallelen Strangs; fehlt es, ist es `false`. */
export function liefertermineFixiert(order: Order): boolean {
  return (order as Record<string, unknown>).transportDatesFixed === true;
}

function transportartVon(order: Order): Transportart {
  const modus = order.transportMode;
  return modus === "air" || modus === "road" ? modus : "see";
}

const TRANSPORT_LABEL: Readonly<Record<Transportart, string>> = {
  see: "See",
  air: "Flug",
  road: "Straße",
};

/** Beschriftung der Transportart für Tabelle und Filter. */
export function transportartLabel(art: Transportart): string {
  return TRANSPORT_LABEL[art];
}

/** Kurzform der Anker für die Herkunftszeile („ETD + 30 T"). */
const ANKER_KURZ: Readonly<Record<Anker, string>> = {
  order_date: "Bestelldatum",
  etd: "ETD",
  eta: "ETA",
  goods_received: "Wareneingang",
};

function versatzText(tage: unknown): string {
  const n = Number(tage ?? 0);
  return Number.isFinite(n) && n !== 0 ? ` ${n > 0 ? "+" : "−"} ${Math.abs(n)} T` : "";
}

interface Einordnung {
  klasse: LiquiditaetsKlasse;
  herkunft: string;
}

function einordnen(
  order: Order,
  rate: OrderPayment,
  datum: string | null,
  basis: Basis,
  anchor: Anker | null,
): Einordnung {
  if (datum === null) {
    if (basis === "hand") return { klasse: "offen", herkunft: "Handdatum fehlt" };
    // Stock Order ohne DSV-Sendung: Abruf steht aus (Spec 2026-10-04). Klasse "offen" = zählt unter „Ohne Datum“.
    if (wartetAufAbruf(order as unknown as Ankerdaten, anchor)) {
      return { klasse: "offen", herkunft: "Stock Order – wartet auf Abruf" };
    }
    const feld = fehlendesAnkerfeld(order as unknown as Ankerdaten, anchor);
    return { klasse: "offen", herkunft: feld ? `${feld} fehlt` : "Ankerdatum fehlt" };
  }
  // Fachentscheid: Status "fixed" ist immer FIX - auch mit Handdatum (`faelligkeitVonRate` liefert dort basis "hand").
  // Ein Handdatum bleibt nur geplant, bis die Rate auf Status fix steht.
  if (paymentStatusOf(rate) === "fixed") return { klasse: "fix", herkunft: "Status fix" };
  if (basis === "eingefroren") return { klasse: "fix", herkunft: "Status fix" };
  if (basis === "hand") return { klasse: "geplant", herkunft: "Handdatum" };
  const versatz = versatzText(rate.dueDays);
  if (basis === "prognose") {
    return { klasse: "geplant", herkunft: `ETD aus PI${versatz} (Prognose)` };
  }
  // basis === "ist"
  const kurz = anchor ? ANKER_KURZ[anchor] : "Anker";
  if (anchor === "order_date" || anchor === "goods_received") {
    return { klasse: "fix", herkunft: `${kurz}${versatz}` };
  }
  const art = transportartVon(order);
  if (art === "air" || art === "road") {
    return liefertermineFixiert(order)
      ? { klasse: "fix", herkunft: `${kurz}${versatz} · Liefertermin fixiert` }
      : { klasse: "geplant", herkunft: `${kurz}${versatz} · Liefertermin nicht fixiert` };
  }
  return order.dsvVerknuepft === true
    ? { klasse: "fix", herkunft: `${kurz}${versatz} · DSV-Sendung` }
    : { klasse: "geplant", herkunft: `${kurz}${versatz} · keine DSV-Sendung` };
}

/**
 * Alle noch nicht bezahlten USD-Raten nicht archivierter Bestellungen, mit Einordnung.
 * Bezahlte Raten (und Raten bezahlter Bestellungen) gehören nicht mehr zum Plan.
 */
export function liquiditaetsRaten(
  orders: readonly Order[],
  suppliers: readonly Supplier[] | null | undefined,
  heute: string,
): LiquiditaetsRate[] {
  const zeilen: LiquiditaetsRate[] = [];
  for (const order of activeReportingOrders(orders, {} as never)) {
    if (orderCurrency(order, suppliers) !== "USD") continue;
    if (isPaid(order)) continue;
    const orderId = String(order.id);
    const orderNo = String(order.orderNo || order.name || "");
    const gemeinsam = {
      order,
      orderId,
      orderNo,
      lieferant: String(order.supplier ?? ""),
      stock: istStockOrder(order),
      transportMode: transportartVon(order),
    };
    const raten = (order.payments || []) as OrderPayment[];
    if (raten.length) {
      raten.forEach((p, index) => {
        if (paymentStatusOf(p) === "paid") return;
        const kopie: OrderPayment = { ...p, due: p.due || (p.dueDate as unknown) || "" };
        const usd = payAmount(order, kopie);
        if (!(usd > 0)) return;
        const { datum, basis } = faelligkeitVonRate(kopie, order);
        const anchor = (kopie.anchor as Anker | null | undefined) ?? null;
        const { klasse, herkunft } = einordnen(order, kopie, datum, basis, anchor);
        zeilen.push({
          ...gemeinsam,
          schluessel: `${orderId}#${p.id ?? index}`,
          label: String(p.label || "Zahlung"),
          usd,
          datum,
          kw: datum ? isoWeekInfo(datum) : null,
          klasse,
          herkunft,
          anchor,
          ueberfaellig: datum !== null && datum < heute,
        });
      });
    } else if (orderTotal(order) > 0) {
      // R5: die Bestellfaelligkeit (listDueDate) ist kein Zahlungsdatum - ohne Ratenplan gibt es keins.
      const datum: string | null = null;
      zeilen.push({
        ...gemeinsam,
        schluessel: `${orderId}#gesamt`,
        label: "Gesamtzahlung",
        usd: orderTotal(order),
        datum,
        kw: null,
        klasse: "offen",
        herkunft: "Fälligkeit fehlt",
        anchor: null,
        ueberfaellig: false,
      });
    }
  }
  return zeilen;
}

// ================= Wochenreihe =================

/** Obergrenze der Wochenreihe (einschließlich der aktuellen Woche). */
export const MAX_WOCHEN = 104;

export interface LiquiditaetsWoche {
  /** `ueberfaellig`, `spaeter` oder der ISO-Wochenschlüssel („2026-W33"). */
  key: string;
  /** Sammelspalte: datierte Raten vor heute (Datum < heute). */
  ueberfaellig: boolean;
  /** Sammelspalte hinter dem Planungshorizont (mehr als `MAX_WOCHEN` Wochen). */
  spaeter: boolean;
  /** Montag der Woche (ISO), `null` bei den Sammelspalten. */
  start: string | null;
  ende: string | null;
  jahr: number | null;
  kw: number | null;
  aktuell: boolean;
  gesichert: number;
  fix: number;
  geplant: number;
  /** Teil von `gesichert`: Kontostand (nur in der aktuellen KW). `kontostand + sicherung` = `gesichert` (bis auf Gleitkomma-Rundung). */
  kontostand: number;
  /** Teil von `gesichert`: offene Sicherungen nach Valutadatum. */
  sicherung: number;
  gesichertKum: number;
  /** Teil von `gesichertKum`: Kontostand, in jeder Spalte (auch Überfällig). */
  kontostandKum: number;
  /** Teil von `gesichertKum`: laufende Summe der Sicherungen. */
  sicherungKum: number;
  fixKum: number;
  geplantKum: number;
  /** gesichertKum − (fixKum + geplantKum) */
  saldo: number;
  /** gesichertKum − fixKum */
  saldoFix: number;
  /** Erste Woche mit `saldo < 0`. */
  reichweite: boolean;
}

/** Offene (noch nicht ins Konto übernommene) gültige Sicherungen. */
export function offeneSicherungen(deals: readonly FxDeal[] | null | undefined): FxDeal[] {
  return (deals ?? []).filter((deal) => dealValid(deal) && deal.snapshotId == null);
}

function datumVon(wert: unknown): string {
  return String(wert ?? "").slice(0, 10);
}

/**
 * Wochenreihe: optional „Überfällig" (Datum < heute, also auch Montag bis gestern der aktuellen
 * KW), dann von der aktuellen KW bis zur letzten datierten KW (Raten oder Sicherungen), Lücken
 * aufgefüllt, höchstens `MAX_WOCHEN` Wochen; was dahinter datiert ist, steht in der letzten
 * Spalte „Später". Raten ohne Datum gehören in keine Woche.
 *
 * `gesichert` = Kontostand (in der aktuellen KW) + offene Sicherungen nach Valutadatum (leer oder
 * vergangen: aktuelle KW). Übernommene Sicherungen (`snapshotId` gesetzt) stecken schon im
 * Kontostand und zählen nicht doppelt.
 *
 * Die kumulierten Werte der Überfällig-Spalte tragen den Kontostand als `gesichertKum`: das Geld
 * liegt heute schon auf dem Konto und deckt Überfälliges - sonst wäre `saldo` dort immer negativ
 * und die „Reichweite" fiele fälschlich auf die Überfällig-Spalte. Die Wochenwerte (`gesichert`)
 * der Spalte bleiben 0, der Kontostand steht je Woche nur in der aktuellen KW.
 */
export function liquiditaetsWochen(
  raten: readonly LiquiditaetsRate[],
  deals: readonly FxDeal[] | null | undefined,
  kontostand: number,
  heute: string,
): LiquiditaetsWoche[] {
  const aktuelleWoche = isoWeekInfo(heute);
  const montag = aktuelleWoche.start;
  const stand = Number.isFinite(kontostand) ? kontostand : 0;

  const sicherungen = offeneSicherungen(deals).map((deal) => {
    const valuta = datumVon(deal.date);
    const datum = valuta === "" || valuta < heute ? heute : valuta;
    return { datum, usd: Number(deal.usd) || 0 };
  });

  const datierte = raten.filter((r) => r.datum !== null);
  const alleDaten = [
    ...datierte.filter((r) => (r.datum as string) >= heute).map((r) => r.datum as string),
    ...sicherungen.map((s) => s.datum),
  ];
  const letztes = alleDaten.reduce((a, b) => (b > a ? b : a), montag);
  const wochenAnzahl = Math.min(
    MAX_WOCHEN,
    Math.floor((Date.parse(`${isoWeekInfo(letztes).start}T12:00:00Z`) - Date.parse(`${montag}T12:00:00Z`)) / (7 * 86_400_000)) + 1,
  );

  const hatUeberfaellig = datierte.some((r) => (r.datum as string) < heute);
  const leer = (): Omit<LiquiditaetsWoche, "key" | "ueberfaellig" | "spaeter" | "start" | "ende" | "jahr" | "kw" | "aktuell"> => ({
    gesichert: 0, kontostand: 0, sicherung: 0, fix: 0, geplant: 0, gesichertKum: 0, kontostandKum: 0, sicherungKum: 0, fixKum: 0, geplantKum: 0, saldo: 0, saldoFix: 0, reichweite: false,
  });

  const wochen: LiquiditaetsWoche[] = [];
  if (hatUeberfaellig) {
    wochen.push({ key: "ueberfaellig", ueberfaellig: true, spaeter: false, start: null, ende: null, jahr: null, kw: null, aktuell: false, ...leer() });
  }
  const jeKey = new Map<string, LiquiditaetsWoche>();
  for (let i = 0; i < wochenAnzahl; i += 1) {
    const info = isoWeekInfo(tageAddieren(montag, i * 7));
    const woche: LiquiditaetsWoche = {
      key: info.key,
      ueberfaellig: false,
      spaeter: false,
      start: info.start,
      ende: info.end,
      jahr: Number(info.year),
      kw: Number(info.week),
      aktuell: i === 0,
      ...leer(),
    };
    wochen.push(woche);
    jeKey.set(woche.key, woche);
  }

  // Hinter dem Horizont: die letzte Wochenspalte endet am `horizontEnde`, alles Spätere sammelt "Später".
  const horizontEnde = wochen[wochen.length - 1].ende as string;
  const hinterHorizont = (datum: string): boolean => datum > horizontEnde;
  const brauchtSpaeter =
    datierte.some((r) => hinterHorizont(r.datum as string)) || sicherungen.some((s) => hinterHorizont(s.datum));
  let spaeter: LiquiditaetsWoche | null = null;
  if (brauchtSpaeter) {
    spaeter = { key: "spaeter", ueberfaellig: false, spaeter: true, start: null, ende: null, jahr: null, kw: null, aktuell: false, ...leer() };
    wochen.push(spaeter);
  }

  if (jeKey.size > 0) {
    const aktuell = jeKey.get(aktuelleWoche.key) as LiquiditaetsWoche;
    aktuell.gesichert += stand;
    aktuell.kontostand += stand;
  }
  for (const s of sicherungen) {
    const ziel = hinterHorizont(s.datum) ? spaeter : jeKey.get(isoWeekInfo(s.datum).key);
    if (ziel) {
      ziel.gesichert += s.usd;
      ziel.sicherung += s.usd;
    }
  }
  const ueberfaellig = wochen[0]?.ueberfaellig ? wochen[0] : null;
  for (const r of datierte) {
    const datum = r.datum as string;
    const ziel = datum < heute ? ueberfaellig : hinterHorizont(datum) ? spaeter : jeKey.get(isoWeekInfo(datum).key);
    if (!ziel || r.klasse === "offen") continue;
    if (r.klasse === "fix") ziel.fix += r.usd;
    else ziel.geplant += r.usd;
  }

  let gesichertKum = 0;
  let sicherungKum = 0;
  let fixKum = 0;
  let geplantKum = 0;
  let reichweiteGesetzt = false;
  for (const woche of wochen) {
    fixKum += woche.fix;
    geplantKum += woche.geplant;
    if (woche.ueberfaellig) {
      woche.gesichertKum = stand;
      woche.kontostandKum = stand;
      woche.sicherungKum = 0;
    } else {
      gesichertKum += woche.gesichert;
      sicherungKum += woche.sicherung;
      woche.gesichertKum = gesichertKum;
      woche.sicherungKum = sicherungKum;
      woche.kontostandKum = stand;
    }
    woche.fixKum = fixKum;
    woche.geplantKum = geplantKum;
    woche.saldo = woche.gesichertKum - (fixKum + geplantKum);
    woche.saldoFix = woche.gesichertKum - fixKum;
    if (!reichweiteGesetzt && woche.saldo < 0) {
      woche.reichweite = true;
      reichweiteGesetzt = true;
    }
  }
  return wochen;
}

// ================= Kennzahlen =================

export interface LiquiditaetsKennzahlen {
  kontostand: number;
  offeneSicherungen: number;
  gesichertGesamt: number;
  fixSumme: number;
  fixAnzahl: number;
  geplantSumme: number;
  geplantAnzahl: number;
  ohneDatumSumme: number;
  ohneDatumAnzahl: number;
  stockSumme: number;
  stockAnzahl: number;
  ueberfaelligSumme: number;
  ueberfaelligAnzahl: number;
  /** Erste Woche mit negativem Saldo (Unterdeckung); null = über den Planungshorizont gedeckt. */
  reichweite: LiquiditaetsWoche | null;
  /** Letzte Kalenderwoche vor der Unterdeckung (Saldo >= 0); null, wenn schon die erste Woche unterdeckt ist. */
  gedecktBis: LiquiditaetsWoche | null;
}

/** Kontostand aus der Kurssicherungsübersicht (Gesamtstand über alle Konten). */
export function kontostandVon(overview: { globalCurrent?: { balanceUsd?: unknown } } | null | undefined): number {
  return Number(overview?.globalCurrent?.balanceUsd ?? 0) || 0;
}

/**
 * Ob die Seite den Kontostand kennt: die Uebersicht ist geladen UND traegt `globalCurrent` (auch `null` zaehlt, das Feld ist da). Sonst
 * zeigt die Kachel den Hinweis "Kontostand unbekannt" und rechnet mit 0 (`kontostandVon`). Stand bis zum KI-Zugang (Stufe 1) als Ausdruck
 * in `LiquiditaetsSeite.tsx`; der Server (`backend/src/ki/auswertung/frontend/`) braucht dieselbe Entscheidung aus derselben Kopie.
 */
export function istKontostandBekannt(overview: { globalCurrent?: unknown } | null | undefined): boolean {
  return overview?.globalCurrent !== undefined && overview !== null;
}

export function liquiditaetsKennzahlen(
  raten: readonly LiquiditaetsRate[],
  wochen: readonly LiquiditaetsWoche[],
  deals: readonly FxDeal[] | null | undefined,
  kontostand: number,
): LiquiditaetsKennzahlen {
  const summe = (zeilen: readonly LiquiditaetsRate[]): number => zeilen.reduce((s, r) => s + r.usd, 0);
  const fix = raten.filter((r) => r.klasse === "fix");
  const geplant = raten.filter((r) => r.klasse === "geplant");
  const ohne = raten.filter((r) => r.klasse === "offen");
  const stock = raten.filter((r) => r.stock);
  const ueber = raten.filter((r) => r.ueberfaellig);
  const sicherungen = offeneSicherungen(deals).reduce((s, d) => s + (Number(d.usd) || 0), 0);
  const reichweite = wochen.find((w) => w.reichweite) ?? null;
  return {
    kontostand,
    offeneSicherungen: sicherungen,
    gesichertGesamt: kontostand + sicherungen,
    fixSumme: summe(fix),
    fixAnzahl: fix.length,
    geplantSumme: summe(geplant),
    geplantAnzahl: geplant.length,
    ohneDatumSumme: summe(ohne),
    ohneDatumAnzahl: ohne.length,
    stockSumme: summe(stock),
    stockAnzahl: stock.length,
    ueberfaelligSumme: summe(ueber),
    ueberfaelligAnzahl: ueber.length,
    reichweite,
    gedecktBis: reichweite
      ? ([...wochen.slice(0, wochen.indexOf(reichweite))].reverse().find((w) => !w.ueberfaellig && !w.spaeter) ?? null)
      : null,
  };
}

// ================= Reihenfolge der Ratentabelle =================
//
// Stand bis zum KI-Zugang (Stufe 1) in `components/LiquiditaetsPage/RatenTabelle.tsx` (`sortiereRaten`, `vergleiche`, `STATUS_REIHE`);
// UNVERAENDERT hierher gezogen, damit der Server die Raten in derselben Reihenfolge nennt wie die Tabelle der Seite (Vorgabe:
// Faelligkeit aufsteigend). Die Komponente ruft diese Funktion.

export type RatenSpalte =
  | "bestellung" | "lieferant" | "rate" | "usd" | "faellig" | "status" | "herkunft" | "stock" | "transport";

export interface RatenSortierung {
  key: RatenSpalte;
  dir: "asc" | "desc";
}

/** Die Sortierung, mit der die Tabelle startet: Faelligkeit aufsteigend. */
export const STANDARD_RATEN_SORTIERUNG: RatenSortierung = { key: "faellig", dir: "asc" };

const STATUS_REIHE: Readonly<Record<LiquiditaetsRate["klasse"], number>> = { fix: 0, geplant: 1, offen: 2 };

function vergleiche(a: LiquiditaetsRate, b: LiquiditaetsRate, key: RatenSpalte): number {
  switch (key) {
    case "bestellung": return a.orderNo.localeCompare(b.orderNo, "de", { numeric: true });
    case "lieferant": return a.lieferant.localeCompare(b.lieferant, "de");
    case "rate": return a.label.localeCompare(b.label, "de");
    case "usd": return a.usd - b.usd;
    case "faellig": return (a.datum ?? "").localeCompare(b.datum ?? "");
    case "status": return STATUS_REIHE[a.klasse] - STATUS_REIHE[b.klasse];
    case "herkunft": return a.herkunft.localeCompare(b.herkunft, "de");
    case "stock": return Number(b.stock) - Number(a.stock);
    case "transport": return a.transportMode.localeCompare(b.transportMode);
  }
}

/** Zeilen ohne Datum stehen in jeder Richtung am Ende ("Offen ans Ende"). Sortiert eine KOPIE. */
export function sortiereRaten(raten: readonly LiquiditaetsRate[], s: RatenSortierung): LiquiditaetsRate[] {
  const faktor = s.dir === "asc" ? 1 : -1;
  return [...raten].sort((a, b) => {
    if (s.key === "faellig" && (a.datum === null) !== (b.datum === null)) return a.datum === null ? 1 : -1;
    const v = vergleiche(a, b, s.key) * faktor;
    return v !== 0 ? v : a.orderNo.localeCompare(b.orderNo, "de", { numeric: true });
  });
}

// ================= Filter (Kachel, Woche) =================

export type KachelFilter = "fix" | "geplant" | "ohneDatum" | "stock" | "ueberfaellig";
export type WochenAuswahl = { key: string; modus: "woche" | "kumuliert" };

export function passtZurKachel(rate: LiquiditaetsRate, kachel: KachelFilter): boolean {
  switch (kachel) {
    case "fix": return rate.klasse === "fix";
    case "geplant": return rate.klasse === "geplant";
    case "ohneDatum": return rate.klasse === "offen";
    case "stock": return rate.stock;
    case "ueberfaellig": return rate.ueberfaellig;
  }
}

/**
 * Passt die Rate zur angeklickten Woche? „Je Woche": Raten dieser KW (Überfällig-Spalte: Raten mit
 * Datum vor heute; Später-Spalte: Raten hinter der letzten Woche). „Kumuliert": alle datierten
 * Raten bis einschließlich dieser KW, überfällige inklusive.
 */
export function passtZurWoche(
  rate: LiquiditaetsRate,
  auswahl: WochenAuswahl,
  wochen: readonly LiquiditaetsWoche[],
  heute: string,
): boolean {
  if (rate.datum === null) return false;
  const ziel = wochen.find((w) => w.key === auswahl.key);
  if (!ziel) return false;
  const letzteWoche = [...wochen].reverse().find((w) => !w.spaeter && !w.ueberfaellig);
  const horizontEnde = letzteWoche?.ende ?? "";
  if (ziel.spaeter) return auswahl.modus === "woche" ? rate.datum > horizontEnde : true;
  if (ziel.ueberfaellig) return rate.datum < heute;
  if (auswahl.modus === "woche") return rate.datum >= heute && rate.kw?.key === ziel.key;
  return rate.datum <= (ziel.ende as string);
}

/** Beschriftung einer Woche: „KW 42 / 2026", „Überfällig" bzw. „Später". */
export function wochenLabel(woche: Pick<LiquiditaetsWoche, "ueberfaellig" | "kw" | "jahr"> & { spaeter?: boolean }): string {
  if (woche.spaeter) return "Später";
  return woche.ueberfaellig ? "Überfällig" : `KW ${String(woche.kw).padStart(2, "0")} / ${woche.jahr}`;
}
